import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import GlassCard from "../../../components/common/GlassCard";

function getRiskColor(level) {
  switch (level) {
    case "low":
      return "success";
    case "medium":
      return "warning";
    case "high":
    case "critical":
      return "error";
    default:
      return "default";
  }
}

function AssessmentsCard({ assessments, onStartAssessment }) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleAddAssessment = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleSelectAssessment = (type) => {
    setDialogOpen(false);
    onStartAssessment(type);
  };

  return (
    <>
      <GlassCard 
        title="Assessments & Screenings" 
        icon={<AssignmentIcon color="info" />}
        onAdd={handleAddAssessment}
        addLabel="Add Assessment"
        data-testid="assessments-card"
        data-card-category="assessments"
      >
        {assessments?.length ? (
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
              COMPLETED ASSESSMENTS
            </Typography>
            {assessments.slice(0, 5).map((assessment) => (
              <Box
                key={assessment.id}
                mb={1.5}
                sx={{
                  p: 1.25,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "background.paper",
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.25}>
                  <Typography variant="body2" fontWeight={500}>
                    {assessment.assessment_name}
                  </Typography>
                  {assessment.risk_level && (
                    <Chip
                      label={assessment.risk_level}
                      size="small"
                      color={getRiskColor(assessment.risk_level)}
                      variant="outlined"
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {new Date(assessment.completed_at).toLocaleDateString()} • {assessment.completed_by}
                </Typography>
                {assessment.total_score !== null && assessment.max_score !== null && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Score: {assessment.total_score}/{assessment.max_score}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        ) : (
          <Alert severity="info" variant="outlined">
            No assessments completed. Click "Add Assessment" to start a screening.
          </Alert>
        )}
      </GlassCard>

      <AssessmentSelectionDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSelectAssessment={handleSelectAssessment}
      />
    </>
  );
}

export function AssessmentSelectionDialog({ open, onClose, onSelectAssessment }) {
  const assessmentTypes = [
    {
      value: "sdoh",
      label: "Social Determinants of Health (SDOH)",
      description: "Evaluates social and economic factors affecting health outcomes",
    },
    {
      value: "fall_risk",
      label: "Fall Risk Assessment",
      description: "Identifies patients at risk for falls and mobility issues",
    },
    {
      value: "home_safety",
      label: "Home Safety Assessment",
      description: "Evaluates home environment for safety hazards",
    },
    {
      value: "nutrition",
      label: "Nutrition Risk Screening",
      description: "Assesses nutritional status and dietary concerns",
    },
    {
      value: "phq9",
      label: "PHQ-9 Depression Screening",
      description: "Standardized depression severity questionnaire",
    },
    {
      value: "gad7",
      label: "GAD-7 Anxiety Assessment",
      description: "Generalized anxiety disorder screening (7 items)",
    },
    {
      value: "adl",
      label: "ADL/IADL Functional Assessment",
      description: "Basic and instrumental activities of daily living (Katz + Lawton)",
    },
    {
      value: "hra",
      label: "Health Risk Assessment (HRA)",
      description: "Comprehensive annual health risk screening (15 items)",
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Select Assessment Type</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {assessmentTypes.map((type) => (
            <Box
              key={type.value}
              sx={{
                p: 2,
                mb: 1.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: "action.hover",
                },
              }}
              onClick={() => onSelectAssessment(type.value)}
              data-testid={`select-assessment-${type.value}`}
              role="button"
              aria-label={`Start ${type.label}`}
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  onSelectAssessment(type.value);
                }
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} mb={0.5}>
                {type.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {type.description}
              </Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}

AssessmentSelectionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectAssessment: PropTypes.func.isRequired,
};

AssessmentsCard.propTypes = {
  assessments: PropTypes.array,
  onStartAssessment: PropTypes.func.isRequired,
};

AssessmentsCard.defaultProps = {
  assessments: [],
};

export default AssessmentsCard;
